import dayjs from "dayjs";
import { ArticleJsonLd } from "next-seo";
import Head from "next/head";
import Image from "next/image";
import * as Router from "next/router";
import React from "react";
import styled from "styled-components";

import { ALLBRIGHT_URL } from "@/config.client";
import { ImageProps } from "next/dist/client/image";
import ActivityPost from "../../../components/ActivityPost";
import Button from "../../../components/Button";
import ClientRender from "../../../components/ClientRender";
import * as I from "../../../components/Icons";
import Link from "../../../components/Link";
import PromoBanner from "../../../components/PromoBanner";
import * as T from "../../../components/Typography";
import UpsellModal from "../../../components/UpsellModal";
import * as UserContext from "../../../components/UserContext";
import WhatsYourMind from "../../../components/WhatsYourMind";
import * as u from "../../../utils";
import * as gtag from "../../../utils/gtag";
import * as h from "../../../utils/hooks";

const MONEY_CATEGORIES = [
  "Investing",
  "Budgeting",
  "Raising Capital",
  "Debt",
  "Money",
];

export type ArticleHeroImage = {
  caption?: string | null;
  image: {
    title?: string;
    img?: string;
    url?: string;
  };
};

export type ContentfulMetadata = {
  tags?: {
    id?: string;
    name?: string;
  }[];
};

type Author = {
  id: string;
  jobTitle: string;
  imageSrc: string;
  name: string;
  articles: string[];
};

type BodyCollection = {
  items: string[];
};

type ArticleSys = {
  id: string;
  firstPublishedAt: number | string;
};

type ArticleComments = {
  results?: {
    id: string;
  }[];
  next: string;
  duration: string; // e.g "9.25ms"
};

interface Article {
  id: string;
  sys: ArticleSys;
  author: Author;
  contentfulMetadata: ContentfulMetadata;
  bodyCollection: BodyCollection;
  description: string | null;
  topic: string;
  firstPublished: number;
  wordCount: number;
  title: string;
  heroImage: ArticleHeroImage;
  articles: string;
  url: string;
}

type TrendingArticles = {
  articles?: Article[];
};

interface ArticleProps extends Article {
  trending: TrendingArticles;
  comments: ArticleComments;
}

export default function Article(props: ArticleProps) {
  const userInfo = React.useContext(UserContext.Context);
  const router = Router.useRouter();
  const [modalType, setModalType] = React.useState<string | false>(false);
  const [isSaved, onToggleSave] = h.useEditToggle("articles", props.sys.id);
  const [isFollowing, onToggleFollow] = h.useEditToggle(
    "authors",
    props.author.id
  );
  const isLoggedIn = !!userInfo.id;
  const content = props.bodyCollection.items.map(getContent);
  const getFirstParagraph = (arr) => arr.find((c) => !!c?.key)?.key;
  const firstParagraph =
    getFirstParagraph(content) || getFirstParagraph(content[0]);
  const description = props.description || u.trimStr(firstParagraph, 160);

  return (
    <S.Background>
      {getHead()}
      <ArticleJsonLd {...getJsonLdProps()} />
      <S.Container
        id="from-top"
        itemScope
        itemType="https://schema.org/NewsArticle"
      >
        <UpsellModal type={modalType} setType={setModalType} />
        {header()}
        <ClientRender>{isLoggedIn && <PromoBanner />}</ClientRender>
        <S.Article itemProp="articleBody" data-cy="article-content">
          {content}
          {MONEY_CATEGORIES.includes(props.topic) && moneyDisclaimer()}
        </S.Article>
        <ClientRender>
          <Comments />
          <Trending articles={props.trending} />
        </ClientRender>
      </S.Container>
    </S.Background>
  );

  function header() {
    const date = props.firstPublished
      ? dayjs.unix(props.firstPublished).format("DD MMMM YYYY")
      : "";
    const schemaDate = dayjs.unix(props.firstPublished).format("YYYY-MM-DD");
    const minsToRead = Math.round(props.wordCount / 200)
      .toString()
      .padStart(2, "0");
    const isLongTitle = props.title.trim().split(" ").length > 15;

    return (
      <S.Header>
        {props.heroImage?.image?.url && (
          <SH.Image data-cy="article-header-image">
            <Image {...getImageProps(props.heroImage.image)} />
            <meta
              itemProp="thumbnailUrl"
              content={props.heroImage.image.url + "?w=350"}
            />
            <meta
              itemProp="image"
              content={props.heroImage.image.url + "?w=350"}
            />
            <TT.CMHero>{props.heroImage.caption}</TT.CMHero>
          </SH.Image>
        )}
        <SH.Info data-cy="article-header-info">
          <SH.Meta>
            <Link href={`/edit/articles?topic=${props.topic}`}>
              <TT.CSB itemProp="genre">{props.topic}</TT.CSB>
            </Link>
            <TT.CS>
              <time dateTime={schemaDate} itemProp="datePublished">
                {date}
              </time>
            </TT.CS>
            <meta itemProp="wordCount" content={`${props.wordCount}`} />
            <meta itemProp="timeRequired" content={`PT${minsToRead}M`} />
            <meta itemProp="abstract" content={description} />
            <meta itemProp="description" content={description} />
          </SH.Meta>
          <TT.H1S isLongTitle={isLongTitle} itemProp="headline">
            {props.title}
          </TT.H1S>
          {author()}
          {userOptions()}
        </SH.Info>
      </S.Header>
    );

    function author() {
      const authorImgProps: ImageProps = {
        src: props.author.imageSrc,
        alt: props.author.name,
        layout: "fill",
        objectFit: "cover",
      };

      const btnProps = {
        text: isFollowing ? "Following" : "Follow",
        isFollowing,
        isSecondary: true,
        onClick: attemptToggleFollow,
      };

      return (
        <SH.Author>
          <Link href={`/edit/articles?author=${props.author.id}`}>
            <SHA.Profile>
              <SHA.Image>
                <Image {...authorImgProps} />
              </SHA.Image>
              <SHA.Bio>
                <TT.CMB>{props.author.name}</TT.CMB>
                <TT.CM>{props.author.jobTitle}</TT.CM>
              </SHA.Bio>
            </SHA.Profile>
          </Link>
          {<Button {...btnProps} />}
        </SH.Author>
      );

      async function attemptToggleFollow() {
        if (await onToggleFollow()) return;
        setModalType("follow");
      }
    }

    function userOptions() {
      const commentCount = props.comments?.results?.length || 0;
      const commentPlural = commentCount === 1 ? "comment" : "comments";

      return (
        <SH.User>
          <SHU.Item onClick={() => router.push("#comments")}>
            <meta
              itemProp="discussionUrl"
              content={ALLBRIGHT_URL + router.asPath + "#comments"}
            />
            <SHU.Comment />
            <TT.A isTop>
              <span itemProp="commentCount">{commentCount}</span>{" "}
              {commentPlural}
            </TT.A>
          </SHU.Item>
          <SHU.Item onClick={attemptToggleSave}>
            <SHU.Bookmark fill={isSaved ? u.colours.charcoal : "transparent"} />
            <TT.A isTop>{isSaved ? "Saved" : "Save"}</TT.A>
          </SHU.Item>
          <SHU.Item>
            <ShareOptions />
          </SHU.Item>
        </SH.User>
      );

      async function attemptToggleSave() {
        if (await onToggleSave()) return;
        setModalType("save");
      }
    }

    function ShareOptions() {
      const pageLink = `${ALLBRIGHT_URL}${router.asPath}`;
      const [hasCopied, onCopy] = h.onCopyText(pageLink) as [
        boolean,
        () => void,
      ];

      return (
        <div>
          <SHU.DropdownHover>
            <SHU.Share />
            <TT.A isTop>Share</TT.A>
            <SHU.DropdownContent>
              {copy()}
              {send()}
              {facebook()}
              {linkedin()}
              {twitter()}
            </SHU.DropdownContent>
          </SHU.DropdownHover>
        </div>
      );

      function copy() {
        return (
          <SHU.ShareItem onClick={onClick}>
            {hasCopied ? <I.Tick grow={1} /> : <I.Hyperlink grow={0.5} />}
            <TT.A>{hasCopied ? "Link copied" : "Copy article link"}</TT.A>
          </SHU.ShareItem>
        );

        function onClick() {
          gtag.event({
            name: gtag.GA_EVENT.EDIT_ENGAGEMENT,
            category: "EDIT",
            label: "Copy article link",
            value: "share_link",
          });

          onCopy();
        }
      }

      function send() {
        return (
          <SHU.ShareItem onClick={handleSend}>
            <I.Plane grow={0.7} />
            <TT.A>Send in message</TT.A>
          </SHU.ShareItem>
        );

        function handleSend() {
          if (!isLoggedIn) return setModalType("share");
          router.push(`/messages?text=${pageLink}`);
        }
      }

      function facebook() {
        return (
          <SHU.ShareItem onClick={() => share("fb")}>
            <I.Facebook grow={1} />
            <TT.A>Share on Facebook</TT.A>
          </SHU.ShareItem>
        );
      }

      function linkedin() {
        return (
          <SHU.ShareItem onClick={() => share("li")}>
            <I.LinkedIn grow={1} />
            <TT.A>Share on LinkedIn</TT.A>
          </SHU.ShareItem>
        );
      }

      function twitter() {
        return (
          <SHU.ShareItem onClick={() => share("tw")}>
            <I.Twitter grow={1} />
            <TT.A>Share on Twitter</TT.A>
          </SHU.ShareItem>
        );
      }

      function share(platform) {
        const launchPopup = (link) =>
          window.open(
            link,
            "share",
            `status=no,location=no,toolbar=no,menubar=no,width=600,height=600,left=100,top=100`
          );
        switch (platform) {
          case "fb":
            return launchPopup(
              `https://www.facebook.com/sharer/sharer.php?u=${pageLink}`
            );
          case "li":
            return launchPopup(
              `https://www.linkedin.com/sharing/share-offsite/?url=${pageLink}`
            );
          case "tw":
            return launchPopup(
              `https://twitter.com/intent/tweet?url=${pageLink}&text=${props.title} by ${props.author.name} for @weareAllBright`
            );
        }
      }
    }
  }

  function getContent(content, idx) {
    switch (content?.__typename) {
      case "Subheading":
        return (
          <TT.H2SSub key={content.subheading}>{content.subheading}</TT.H2SSub>
        );
      case "Quote":
        return getQuote();
      case "ImageSingle":
        return getImageSingle();
      case "ImageDouble":
        return getImageDouble();
      case "Paragraph":
        return content.paragraph?.json?.content.map(getRichText);
      default:
        return null;
    }

    function getRichText(elm) {
      const paragraphContent = elm?.content?.map(formatNodes);

      return (
        <TT.CL key={paragraphContent[0]?.key + idx}>{paragraphContent}</TT.CL>
      );

      function formatNodes(content) {
        switch (content.nodeType) {
          case "text":
            return <span key={content.value}>{addMarks(content)}</span>;
          case "hyperlink":
            return getLinkOrIframe(content);
          default:
            return null;
        }

        function addMarks(text) {
          if (text.marks.length === 2)
            return (
              <strong>
                <em>{text.value}</em>
              </strong>
            );
          if (text.marks[0]?.type === "bold")
            return <strong>{text.value}</strong>;
          if (text.marks[0]?.type === "italic") return <em>{text.value}</em>;

          return text.value;
        }

        function getLinkOrIframe(link) {
          if (link.data.uri.endsWith("/embed")) {
            return (
              <S.Iframe
                key={link.data.uri}
                src={link.data.uri}
                title={link.content[0]}
              />
            );
          }

          return (
            <TT.ABody
              itemProp="url"
              href={link.data.uri}
              key={link.data.uri}
              target="_blank"
            >
              {addMarks(link.content[0])}
            </TT.ABody>
          );
        }
      }
    }

    function getQuote() {
      return (
        <SA.Quote key={content.quote}>
          <TT.H2SQuote isLong={content.quote.length > 100}>
            {content.quote}
          </TT.H2SQuote>
          <TT.CMQuote>{content.attribution}</TT.CMQuote>
        </SA.Quote>
      );
    }

    function getImageSingle() {
      const objectFit = content.fullWidth ? "cover" : "contain";

      if (!content.image?.url) return null;

      return (
        <SA.ImageSingleContainer key={content.image.url}>
          <SA.Image>
            <Image {...getImageProps(content.image)} objectFit={objectFit} />
          </SA.Image>
          <TT.CS center={!content.fullWidth}>{content.caption}</TT.CS>
        </SA.ImageSingleContainer>
      );
    }

    function getImageDouble() {
      if (!(content.image1?.url && content.image2?.url)) return null;

      return (
        <SA.ImageDoubleContainer key={content.image1.url + content.image2.url}>
          <SA.ImageLeft>
            <SA.Image>
              <Image {...getImageProps(content.image1)} />
            </SA.Image>
            <T.CS>{content.caption1}</T.CS>
          </SA.ImageLeft>
          <SA.ImageRight>
            <SA.Image>
              <Image {...getImageProps(content.image2)} />
            </SA.Image>
            <T.CS>{content.caption2}</T.CS>
          </SA.ImageRight>
        </SA.ImageDoubleContainer>
      );
    }
  }

  function Comments() {
    const { store, onCreatePost } = h.useStreamFeed("public", props.sys.id);
    const comments = store || props.comments;

    return (
      <S.Comments>
        <SC.ScrollTo id="comments" />
        <TT.H2S>Comments</TT.H2S>
        <div onClick={() => !isLoggedIn && setModalType("comment")}>
          <WhatsYourMind
            onCreatePost={onCreatePost}
            location="Edit"
            placeholder="What do you think?"
          />
        </div>
        {comments?.results?.map(getComment)}
      </S.Comments>
    );

    function getComment(elm) {
      const postProps = {
        key: elm.id,
        type: "comment",
        link: router.asPath,
        pageId: props.id,
        ...elm,
      };

      if (userInfo.id) return <ActivityPost {...postProps} />;

      return (
        <div
          key={elm.id}
          onClick={() => setModalType("comment")}
          itemProp="comment"
          itemScope
          itemType="https://schema.org/Comment"
        >
          <ActivityPost {...elm} />
        </div>
      );
    }
  }

  function moneyDisclaimer() {
    return (
      <>
        <TT.H2SSub>Disclaimer</TT.H2SSub>
        <TT.CL>
          AllBright cannot guarantee that all of the information provided in
          this video or article is accurate. Use the information provided on our
          website at your own risk. If you wish to make an investment you should
          seek independent financial advice before doing so, and ensure that you
          have carried out your own research on the product or company that you
          are investing in. Any advice provided is not tailored to anyone’s
          individual situation, as each individual is in a different situation.
          AllBright does not accept any liability whatsoever for any action
          taken or losses incurred as a result of the information provided on
          our site.
        </TT.CL>
      </>
    );
  }

  function getHead() {
    const title = u.trimStr(props.title, 50) + " | Post | AllBright";
    const info = {
      title,
      description,
      imgW: 1200,
      imgH: 627,
      imgUrl: props.heroImage.image.url,
      imgAlt: props.heroImage.image.title,
      url: ALLBRIGHT_URL + router.asPath,
    };

    const typeInfo = {
      author: `${ALLBRIGHT_URL}/edit/articles?author=${props.author.id}`,
      tag: props.topic,
      published_time: dayjs
        .unix(props.firstPublished)
        .format("YYYY-MM-DDTHH:mm:ssZ[Z]"),
    };

    const metaTags = [
      ...u.generateMetaTags(info),
      ...u.generateMetaType("article", typeInfo),
    ];

    return (
      <Head>
        <title>{title}</title>
        {metaTags.map((elm) => (
          <meta {...elm} key={elm.name || elm.itemprop || elm.property} />
        ))}
      </Head>
    );
  }

  function getJsonLdProps() {
    return {
      url: `${ALLBRIGHT_URL}${router.asPath}`,
      title: props.title,
      description,
      images: [props.heroImage.image.url + "?w=600"],
      datePublished: dayjs
        .unix(props.firstPublished)
        .format("YYYY-MM-DDTHH:mm:ssZ[Z]"),
      dateModified: dayjs
        .unix(props.firstPublished)
        .format("YYYY-MM-DDTHH:mm:ssZ[Z]"),
      authorName: [props.author.name],
      publisherName: props.author.name,
      publisherLogo: props.author.imageSrc,
    };
  }
}

function getImageProps(image): ImageProps {
  return {
    src: image.url,
    alt: image.title,
    layout: "fill",
    objectFit: "cover",
  };
}

function Trending(props) {
  return (
    <S.Trending>
      <TT.H2S>Trending</TT.H2S>
      <ST.Carousel>{props.articles?.map(getArticle)}</ST.Carousel>
    </S.Trending>
  );

  function getArticle(elm) {
    return (
      <ST.Card key={elm.url} onClick={() => triggerGA(elm)}>
        {elm.heroImage?.image?.url && (
          <Link href={`/edit/articles/${elm.url}`}>
            <ST.Image>
              <Image {...getImageProps(elm.heroImage.image)} />
            </ST.Image>
          </Link>
        )}
        <Link href={`/edit/articles?topic=${elm.topic}`}>
          <TT.CSBTrending>{elm.topic}</TT.CSBTrending>
        </Link>
        <Link href={`/edit/articles/${elm.url}`}>
          <TT.CMClick>{elm.title}</TT.CMClick>
        </Link>
      </ST.Card>
    );

    function triggerGA(elm) {
      gtag.event({
        name: gtag.GA_EVENT.EDIT_ENGAGEMENT,
        category: "EDIT",
        label: "From article",
        value: elm.title,
      });
    }
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps(ctx) {
  const axios = require("axios");
  const firebase = require("../../../firebase.admin");
  const config = require("../../../config");
  const db = firebase.firestore();

  try {
    const addFbInfo = (article) => {
      const fbArticle = articles.find((a) => a.id === article.sys.id);
      const fbAuthor =
        authors.find((a) => a.email === article.author) ||
        authors.find((a) => a.email === "digital@allbright.co");
      const firstPublished =
        fbArticle?.firstPublished ||
        dayjs(article.sys.firstPublishedAt).unix() ||
        null;
      return { ...article, ...fbArticle, author: fbAuthor, firstPublished };
    };
    const articleId = (await axios.post(config.CONTENTFUL_EDIT, getArticleId()))
      .data.data.articleCollection.items[0].sys.id;
    const gqlArticle = (
      await axios.post(config.CONTENTFUL_EDIT, getArticleQuery(articleId))
    ).data.data.article;
    const articles = (
      await db.collection("articles").where("score", ">", 1).get()
    ).docs.map((d) => d.data());
    const authors = (await db.collection("authors").get()).docs.map((d) =>
      d.data()
    );
    const article = addFbInfo(gqlArticle);
    const trendingIds = articles
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((a) => a.id);
    const trending = (
      await axios.post(config.CONTENTFUL_EDIT, getTrendingQuery(trendingIds))
    ).data.data.articleCollection.items;
    const comments = await getPageComments(articleId);

    return { props: { ...article, trending, comments }, revalidate: 3600 };
  } catch (err) {
    console.error("✘ EDIT article error: ", err);
    return { notFound: true };
  }

  async function getPageComments(id) {
    const stream = require("getstream");
    const currClient = stream.connect(
      config.STREAM_PUBLIC,
      config.STREAM_SECRET,
      config.STREAM_APP_ID
    );
    const baseOpts = { limit: 30, reactions: { recent: true, counts: true } };
    return await currClient?.feed("public", id).get(baseOpts);
  }

  function getArticleId() {
    return {
      query: `{
        articleCollection(where: {url: "${ctx.params.article}"}) {
          items {sys {id}}
        }
      }`,
    };
  }

  function getArticleQuery(id) {
    return {
      query: `{
        article(id: "${id}") {
          author
          description
          title
          topic
          url
          sys {id firstPublishedAt}
          heroImage {
            caption
            image {title url}
          }
          contentfulMetadata {
            tags {
              id
              name
            }
          }
          bodyCollection {
            items {
              __typename
              ... on Subheading {subheading}
              ... on Quote {attribution quote}
              ... on Paragraph {paragraph {json}}
              ... on ImageSingle {
                caption
                fullWidth
                image {title url}
              }
              ... on ImageDouble {
                caption1
                caption2
                image1 {title url}
                image2 {title url}
              }
            }
          }
        }
      }`,
    };
  }

  function getTrendingQuery(ids) {
    const trendingIdsString = `["${ids.join('", "')}"]`;

    return {
      query: `{
        articleCollection(where: {sys: { id_in: ${trendingIdsString}}}) {
          items {
            title
            topic
            url
            heroImage {
              image {title url}
            }
          }
        }
      }`,
    };
  }
}

type StyleProps = {
  isLongTitle?: boolean;
  isLong?: boolean;
  center?: boolean;
  isTop?: boolean;
};

const S = () => {};

S.Background = styled.div`
  background: white;
  min-height: calc(100vh - 60px);
  width: 100vw;
`;

S.Container = styled.div`
  margin: 0 auto;
  max-width: 1440px;
  padding: 0 16px 100px;
  @media ${u.device.tablet} {
    padding: 40px 40px 100px;
  }
`;

S.Header = styled.header`
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 60px;
  min-height: 400px;
  @media ${u.device.tablet} {
    flex-direction: row;
    height: calc(100vh - 84px - 80px);
    margin-bottom: 80px;
    max-height: 650px;
  }
`;

S.Paywall = styled.div`
  position: relative;
`;

S.Article = styled.div<StyleProps>`
  margin-bottom: 80px;
`;

S.Iframe = styled.iframe`
  border: 0;
  width: 100%;
`;

S.Comments = styled.div`
  margin: 0 auto 80px;
  max-width: 780px;
  position: relative;
`;

S.Trending = styled.div``;

const SH = () => {};

SH.Image = styled.div`
  flex: 1;
  margin-left: -16px;
  min-height: 450px;
  position: relative;
  width: 100vw;
  @media ${u.device.tablet} {
    height: 100%;
    margin-left: 0;
  }
`;

SH.Info = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: 40px 0 60px;
  @media ${u.device.tablet} {
    padding: 60px 0 60px 60px;
  }
`;

SH.Meta = styled.div`
  display: flex;
  margin-bottom: 40px;
  text-transform: uppercase;
`;

SH.Author = styled.div`
  display: flex;
  height: 40px;
  justify-content: space-between;
  margin: 40px 0;
  @media (min-width: 520px) {
    justify-content: initial;
  }
`;

SH.User = styled.ul`
  display: flex;
  > * + * {
    margin-left: 40px;
  }
  @media (min-width: 520px) {
    display: grid;
    grid-template-columns: 2fr 2fr 2fr;
    max-width: 300px;
  }
`;

const SHA = () => {};

SHA.Profile = styled.div`
  cursor: pointer;
  display: flex;
  @media (min-width: 350px) {
    margin-right: 35px;
  }
`;

SHA.Image = styled.div`
  border-radius: 50%;
  height: 100%;
  overflow: hidden;
  position: relative;
  min-width: 40px;
`;

SHA.Bio = styled.div`
  margin-left: 10px;
  padding-right: 5px;
`;

const SHU = () => {};

SHU.Comment = styled(I.Comment)`
  margin-right: 10px;
`;

SHU.Bookmark = styled(I.Bookmark)`
  margin-right: 10px;
`;

SHU.Share = styled(I.Share)`
  margin-right: 10px;
`;

SHU.Item = styled.li`
  align-items: center;
  cursor: pointer;
  display: flex;
  list-style: none;
`;

SHU.DropdownContent = styled.div`
  background: white;
  border: 1px solid ${u.colours.charcoalFaded};
  border-radius: 3px;
  box-shadow: 0 0 5px 1px ${u.colours.charcoalFaded};
  display: none;
  font-size: 1.2rem;
  left: -20px;
  line-height: 3rem;
  min-width: 200px;
  padding: 35px 20px 17.5px;
  position: absolute;
  top: -15px;
  z-index: -2;
  > div {
    margin-top: 20px;
  }
`;

SHU.DropdownHover = styled.div`
  align-items: center;
  cursor: default;
  display: flex;
  position: relative;
  z-index: 3;
  > a::after {
    display: none;
  }
  :hover {
    ${SHU.DropdownContent} {
      display: flex;
      flex-direction: column;
    }
  }
`;

SHU.ShareItem = styled.div`
  align-items: center;
  cursor: pointer;
  display: grid;
  font-size: 12px;
  gap: 10px;
  grid-template-columns: 22px auto;
  grid-template-rows: 22px;
  justify-items: center;
`;

const SP = () => {};

SP.Remaining = styled.div`
  background: rgb(243, 233, 226);
  cursor: pointer;
  margin: 60px 0;
  padding: 20px;
  text-align: center;
`;

SP.Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 40px;
  z-index: 1;
`;

const SA = () => {};

SA.Quote = styled.div`
  margin: 40px auto 60px;
  max-width: 1200px;
  @media ${u.device.mobile} {
    margin: 80px auto;
  }
`;

SA.Image = styled.div`
  height: 100%;
  margin-bottom: 10px;
  position: relative;
  width: 100%;
`;

SA.ImageSingleContainer = styled.div`
  height: 450px;
  margin: 40px -16px 100px;
  width: 100vw;
  @media ${u.device.tablet} {
    height: 792px;
    margin: 80px auto;
    width: 100%;
  }
`;

SA.ImageDoubleContainer = styled.div`
  margin: 60px auto 100px;
  width: 100%;
  @media ${u.device.mobile} {
    display: grid;
    gap: 40px;
    grid-template-columns: 1fr 1fr;
    height: 67.5vw;
    margin-top: 80px;
    width: calc(100% - 80px);
  }
`;

SA.ImageLeft = styled.div`
  height: 120vw;
  margin-bottom: 50px;
  width: 100%;
  @media ${u.device.mobile} {
    height: 75%;
    margin: 0;
    width: initial;
  }
`;

SA.ImageRight = styled(SA.ImageLeft)`
  @media ${u.device.mobile} {
    align-self: flex-end;
  }
`;

const SC = () => {};

SC.ScrollTo = styled.div`
  position: absolute;
  top: -90px;
  height: 0;
  @media ${u.device.tablet} {
    top: -120px;
  }
`;

const ST = () => {};

ST.Carousel = styled.div`
  display: flex;
  overflow-x: auto;
  -ms-overflow-style: none; /* IE 11 */
  scrollbar-width: none; /* Firefox 64 */
  -webkit-overflow-scrolling: touch;
  ::-webkit-scrollbar {
    height: 0;
    width: 0;
  }
  > * + * {
    margin-left: 16px;
  }
  @media ${u.device.tablet} {
    > * + * {
      margin-left: 40px;
    }
  }
`;

ST.Card = styled.div`
  min-width: 252px;
  @media ${u.device.tablet} {
    min-width: 310px;
  }
`;

ST.Image = styled.div`
  cursor: pointer;
  position: relative;
  width: 100%;
  height: 252px;
  @media ${u.device.tablet} {
    height: 310px;
  }
`;

const TT = () => {};

TT.H1S = styled(T.H2Rox)<StyleProps>`
  font-size: 3.2rem;
  line-height: 3.8rem;
  margin-bottom: 25px;
  ${(p) =>
    !p.isLongTitle &&
    `
    @media ${u.device.mobile} {
      font-size: 4.8rem;
      line-height: 5.8rem;
    }
  `}
`;

TT.H2S = styled(T.H3Rox)`
  margin-bottom: 20px;
  @media ${u.device.mobile} {
    font-size: 3.2rem;
    line-height: 3.8rem;
  }
`;

TT.H2SSub = styled(T.H3)`
  max-width: 780px;
  margin: 0 auto 20px;
`;

TT.H2SQuote = styled(T.H3Rox)<StyleProps>`
  font-size: 3.2rem;
  line-height: 3.8rem;
  ${(p) =>
    !p.isLong &&
    `
    @media ${u.device.mobile} {
      font-size: 6.4rem;
      line-height: 7.7rem;
    }
  `}
`;

TT.CXL = styled(T.CXL)`
  margin: 25px auto;
  max-width: 90%;
  @media ${u.device.mobile} {
    max-width: 60%;
  }
`;

TT.CL = styled(T.CL)`
  color: ${u.colours.charcoal};
  font-weight: 400;
  margin: 0 auto 30px;
  max-width: 780px;

  @media ${u.device.mobile} {
    font-size: 1.8rem;
    line-height: 2.6rem;
  }
`;

TT.CM = styled(T.CM)`
  opacity: 1;
`;

TT.CMHero = styled(T.CM)`
  bottom: -30px;
  left: 16px;
  position: absolute;

  @media ${u.device.tablet} {
    left: 0;
  }
`;

TT.CMQuote = styled(T.CM)`
  margin-top: 20px;
`;

TT.CMClick = styled(TT.CM)`
  cursor: pointer;
`;

TT.CMB = styled(TT.CMClick)`
  font-weight: bold;
`;

TT.CS = styled(T.CS)<StyleProps>`
  opacity: 1;
  margin: 0 16px;
  ${(p) => p.center && "text-align: center;"}
  @media ${u.device.mobile} {
    margin-right: 20px;
  }
`;

TT.CSB = styled(TT.CS)`
  cursor: pointer;
  font-weight: bold;
  letter-spacing: 0.15rem;
  margin: 0;
`;

TT.CSBTrending = styled(TT.CSB)`
  margin: 20px auto 10px 0;
  text-transform: uppercase;
`;

TT.A = styled(T.A)<StyleProps>`
  font-weight: 700;
  font-size: 1.6rem;
  line-height: 2rem;
  margin: 0;
  ${(p) => (p.isTop ? "display: none;" : "color: red")}

  @media ${u.device.mobile} {
    display: initial;
    font-size: initial;
    margin-right: auto;
  }
`;

// can't use T.A since bold text used in articles, and long links need breaking
TT.ABody = styled.a`
  color: ${u.colours.charcoal};
  font-weight: 700;
  border-bottom: 2px solid ${u.colours.charcoal};
  text-decoration: none;
`;
